<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div style="width: 100%;">
		<div class="d-flex align-center flex-fill pa-3" style="background-color: #fff;">
			<k-text-field
				v-model="filter.term"
				label="Search by title"
				prepend-inner-icon="fa fa-filter"
				clear-icon="fa fa-times-circle"
				background-color="#fff"
				class="pr-3"
				clearable
				single-line
				hide-details
				outlined
				dense
			/>

			<k-autocomplete
				v-model="filter.user_id"
				label="Author / Reviewer"
				:items="payment_users"
				item-text="user_name"
				item-value="user_id"
				background-color="#fff"
				class="pr-3"
				clearable
				single-line
				hide-details
				outlined
				dense
			/>

			<template v-if="!completed">
				<k-menu v-model="submitted_menu_open" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
					<template v-slot:activator="{ on, attrs }">
						<k-text-field
							:value="submitted_date_range_text"
							placeholder="Submitted Date"
							prepend-inner-icon="fa fa-calendar-days"
							background-color="#fff"
							class="pr-3"
							readonly
							clearable
							hide-details
							outlined
							dense
							v-bind="attrs"
							v-on="on"
							@click:clear="filter.submitted_dates = []"
						/>
					</template>
					<v-date-picker v-model="filter.submitted_dates" range no-title scrollable />
				</k-menu>

				<k-menu v-model="publish_menu_open" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
					<template v-slot:activator="{ on, attrs }">
						<k-text-field
							:value="publish_date_range_text"
							placeholder="Published Date"
							prepend-inner-icon="fa fa-calendar-days"
							background-color="#fff"
							readonly
							clearable
							hide-details
							outlined
							dense
							v-bind="attrs"
							v-on="on"
							@click:clear="filter.publish_dates = []"
						/>
					</template>
					<v-date-picker v-model="filter.publish_dates" range no-title scrollable />
				</k-menu>
			</template>

			<template v-if="completed">
				<k-menu v-model="complete_menu_open" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
					<template v-slot:activator="{ on, attrs }">
						<k-text-field
							:value="complete_date_range_text"
							placeholder="Payments Complete Date"
							prepend-inner-icon="fa fa-calendar-days"
							background-color="#fff"
							class="pr-3"
							readonly
							clearable
							hide-details
							outlined
							dense
							v-bind="attrs"
							v-on="on"
							@click:clear="filter.complete_dates = []"
						/>
					</template>
					<v-date-picker v-model="filter.complete_dates" range no-title scrollable />
				</k-menu>
			</template>

			<k-btn v-if="completed" color="primary" class="ml-auto" @click="load_assets">
				Load Records
			</k-btn>
		</div>

		<k-data-table
			:items="filtered_items"
			:headers="headers"
			:options.sync="options"
			:no-data-text="completed ? 'Use the filters above and click Load Records to view completed payments.' : 'No data available'"
			class="k-asset-manager-table"
			width="100%"
			single-expand
			hide-default-footer
			fixed-header
			light
		>
			<template v-slot:item="{ item, index }">
				<tr class="k-table-row" @click="select_asset(item, index)">
					<td v-for="(header, i) in headers" :key="`column_` + i" :class="{'center-text': header.align == 'center'}">
						<k-icon v-if="header.value == 'button'" class="button" small>fas fa-arrow-up-right-from-square</k-icon>

						<k-icon v-if="header.value == 'payment_complete'" :color="item[header.value] ? 'green' : ''" small>{{ item[header.value] ? 'fas fa-check-square' : 'fas fa-circle-xmark'}}</k-icon>

						<span v-else v-html="display_value(item, header.value)"></span>
					</td>
				</tr>
			</template>
		</k-data-table>

		<AssetPaymentDialog
			v-if="selected_asset"
			:selected_asset="selected_asset"
			@updated="payment_updated"
			@done="selected_asset = null; selected_asset_index = null"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetPaymentDialog from './AssetPaymentDialog.vue';

export default {
	name: 'AssetPaymentTracking',

	components: {AssetPaymentDialog},

	props: {
		completed: {type: Boolean, default: false}
	},

	data() {
		return {
			selected_asset: null,
			selected_asset_index: null,
			adding_payment: null,
			filter: {
				term: undefined,
				user_id: null,
				submitted_dates: [],
				publish_dates: [],
				complete_dates: [],
			},
			payment_users: [],
			submitted_menu_open: false,
			publish_menu_open: false,
			complete_menu_open: false,
			roles: [
				{key: 'author', title: 'Author'},
				{key: 'reviewer', title: 'Reviewer'},
				{key: 'publisher', title: 'Publisher'},
			],
			items: [],
			options: {
				itemsPerPage: -1,
				sortBy: [],
				sortDesc: [false],
			},
			pending_headers: [
				{ text: 'Title', align: 'start', sortable: true, width: '200px', value: 'asset_title' },
				{ text: 'Published Date', align: 'center', sortable: true, value: 'publish_date' },
				{ text: 'Author', align: 'center', sortable: true, value: 'author_name' },
				{ text: 'Author Paid', align: 'center', sortable: true, value: 'author_payments' },
				{ text: 'Reviewers', align: 'center', sortable: true, value: 'reviewers' },
				{ text: 'Reviewers Paid', align: 'center', sortable: true, value: 'reviewer_payment' },
				{ text: '', value: 'data-table-expand' },
			],
			completed_headers: [
				{ text: 'Title', align: 'start', sortable: true, width: '200px', value: 'asset_title' },
				{ text: 'Payments Complete', align: 'center', sortable: true, value: 'payment_complete_date' },
				{ text: 'Author', align: 'center', sortable: true, value: 'author_name' },
				{ text: 'Author Paid', align: 'center', sortable: true, value: 'author_payments' },
				{ text: 'Reviewers', align: 'center', sortable: true, value: 'reviewers' },
				{ text: 'Reviewers Paid', align: 'center', sortable: true, value: 'reviewer_payment' },
				{ text: '', value: 'data-table-expand' },
			]
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		headers() {
			return this.completed ? this.completed_headers : this.pending_headers
		},

		submitted_date_range_text() {
			let sorted = [...this.filter.submitted_dates].sort()
			if (sorted.length == 1) return this.date_format(sorted[0])
			if (sorted.length == 2) return this.date_format(sorted[0]) + ' – ' + this.date_format(sorted[1])
			return ''
		},

		publish_date_range_text() {
			let sorted = [...this.filter.publish_dates].sort()
			if (sorted.length == 1) return this.date_format(sorted[0])
			if (sorted.length == 2) return this.date_format(sorted[0]) + ' – ' + this.date_format(sorted[1])
			return ''
		},

		complete_date_range_text() {
			let sorted = [...this.filter.complete_dates].sort()
			if (sorted.length == 1) return this.date_format(sorted[0])
			if (sorted.length == 2) return this.date_format(sorted[0]) + ' – ' + this.date_format(sorted[1])
			return ''
		},

		filtered_items() {
			let arr = this.items.filter(x => {
				let include = true

				if (this.filter.term) {
					let re = new RegExp(this.filter.term, 'i')
					if (x.asset_title.search(re) == -1) include = false
				}

				if (include && this.filter.user_id) {
					let uid = this.filter.user_id
					let is_author = x.author_id == uid
					let is_reviewer = x.reviewer_data && x.reviewer_data.some(r => r.user_id == uid)
					if (!is_author && !is_reviewer) include = false
				}

				if (include && this.filter.submitted_dates.length == 2) {
					let [from, to] = [...this.filter.submitted_dates].sort()
					let d = x.submitted_date ? x.submitted_date.substring(0, 10) : ''
					if (!d || d < from || d > to) include = false
				}

				if (include && this.filter.publish_dates.length == 2) {
					let [from, to] = [...this.filter.publish_dates].sort()
					let d = x.publish_date ? x.publish_date.substring(0, 10) : ''
					if (!d || d < from || d > to) include = false
				}

				return include
			})

			// then sort
			let sort_by = this.options.sortBy[0] || 'submitted_date'
			arr.sort((a,b) => {
				let val = 0

				if (sort_by == 'title') {
					val = U.natural_sort(a.asset_title, b.asset_title)
				} else {
					if ((a[sort_by] ?? 0 + '') < (b[sort_by] ?? 0 + '')) {
						val = -1
					} else if ((a[sort_by] ?? 0 + '') > (b[sort_by] ?? 0 + '')) {
						val = 1
					}
				}

				// if we get to here, return val or -1 * val
				return this.options.sortDesc[0] ? -1 * val : val
			})

			return arr
		},
	},

	async mounted() {
		if (!this.completed) {
			await this.load_assets()
			this.payment_users = this.derive_payment_users()
		} else {
			await this.load_payment_users()
		}
	},

	methods: {
		async load_assets() {
			let payload = {
				user_id: this.user_info.user_id,
				completed: this.completed ? 1 : 0,
			}

			if (this.completed) {
				if (this.filter.term) payload.search_term = this.filter.term
				if (this.filter.user_id) payload.user_id_filter = this.filter.user_id
				if (this.filter.complete_dates.length == 2) {
					let sorted = [...this.filter.complete_dates].sort()
					payload.complete_date_from = sorted[0]
					payload.complete_date_to = sorted[1]
				}
			}

			U.loading_start()
			let result = await new Promise(resolve => U.ajax('get_asset_review_payment', payload, resolve))
			U.loading_stop()

			if (result.status != 'ok') {
				console.error('load_assets error: ', result)
			} else {
				this.items = result.payment_data ?? []
			}
		},

		async load_payment_users() {
			let payload = { user_id: this.user_info.user_id, completed: this.completed ? 1 : 0 }
			let result = await new Promise(resolve => U.ajax('get_payment_users', payload, resolve))
			if (result.status == 'ok') this.payment_users = result.users ?? []
		},

		derive_payment_users() {
			let users = {}
			this.items.forEach(item => {
				if (item.author_id && item.author_name) users[item.author_id] = item.author_name
				if (item.reviewer_data) {
					item.reviewer_data.forEach(r => {
						if (r.user_id && r.user_name) users[r.user_id] = r.user_name
					})
				}
			})
			return Object.entries(users)
				.map(([user_id, user_name]) => ({ user_id: parseInt(user_id), user_name }))
				.sort((a, b) => a.user_name.localeCompare(b.user_name))
		},
		
		select_asset(asset, index) {
			this.selected_asset = asset
			this.selected_asset_index = index
		},

		display_value(item, key) {
			let value = item[key]
			
			if (key.includes('payments')) {
				if (!empty(value)){
					let amount = 0

					value.forEach(x => { amount += parseInt(x.amount)})

					value = '$' + amount
				} else {
					value = '$0'
				}
			} else if (key.includes('date') || key.includes('_at')) {
				value = this.date_format(value)
			} else if (key == 'asset_title') {
				let icon = U.directive_type_icon(item.asset_type)
				value = `<i class="mr-1 fas ${icon}"></i>${value}`
			} else if (key == 'amount') {
				value = '$' + value
			} else if (key == 'reviewers') {
				let reviewers = (item.reviewer_data || []).map(x => x.user_name)
				value = reviewers.join(' / ')
			} else if (key == 'reviewer_payment') {
				let reviewer_payment = (item.reviewer_data || []).map(x => {
					if (x.payments) {
						let total = 0
						x.payments.forEach(payment => {
							total += parseInt(payment.amount)
						})
						return '$' + total
					} else {
						return '$0'
					}
				})
				value = reviewer_payment.join(' / ')
			}

			return value
		},

		date_format(date_to_format) {
			if (date_to_format) {
				return date.format(new Date(date_to_format), 'M/D/YYYY', (new Date()).getTimezoneOffset())
			} else {
				return '-'
			}
		},

		payment_updated(asset, close) {
			if (this.completed == asset.payment_complete) {
				if (this.selected_asset_index){
					this.items.splice(this.selected_asset_index, 1, asset)
				} else {
					this.items.push(asset)
				}
			} else {
				this.items.splice(this.selected_asset_index, 1)
				this.selected_asset_index = null
			}

			if (close) {
				this.selected_asset = null
				this.selected_asset_index = null
			}

			this.$inform({text: 'Payments Updated'})
		},
	},
}
</script>

<style lang="scss">
.k-asset-manager-table {
	height: 100%;
	width: 100%;

	td, th {
		font-size:12px!important;
		line-height:15px;
	}
	th {
		white-space: nowrap;
		height:32px!important;
	}

	.k-table-row {
		cursor: pointer;
		.button {
			cursor: pointer;
		}

		.btn-column {
			width:5px;
			padding-left:8px!important;
			padding-right:0!important;
		}
		.title-column {
			width: 340px;
		}
		.subject-column {
			max-width:160px;
		}
		.nobr-column {
			white-space:nowrap;
		}

		.center-text {
			text-align: center;
		}

		.tooltip {
			position: relative;
			display: inline-block;
		}

		/* Tooltip text */
		.tooltip .tooltip-text {
			visibility: hidden;
			bottom: 15px;
			left: -15px;
			background-color: black;
			color: #fff;
			text-align: center;
			padding: 5px 6px;
			border-radius: 6px;
			position: absolute;
			z-index: 100;
			font-family: 'Helvetica Neue, Helvetica, Arial, sans-serif';
		}

		/* Show the tooltip text when you mouse over the tooltip container */
		.tooltip:hover .tooltip-text {
			visibility: visible;
		}
	}

	.k-asset-manager-table-no-alignments {
		font-weight:bold;
		color:$v-red-darken-3;
	}
}
</style>